// src/pages/Appointments.tsx
import React from 'react'
import { api, atlas, doctorMe, educationMe, techMe, propertyMe, financialMe, alliedMe, legalMe, migrationMe, ProviderProfession, enabledStacks } from '../api'
import Spinner from '../components/Spinner'
import SlotPickerSheet from '../components/SlotPickerSheet'
import { useLocation, Link, useNavigate, useParams } from 'react-router-dom'
import { CalendarCheck, ExternalLink, MessageSquare } from 'lucide-react'
import { RatingStarsDisplay, RatingStarsInput } from '../components/RatingStars'
import { useNotifications } from '../notifications/NotificationsContext'
import { ProfessionKey, PROFESSIONS, resolveProfessionParam } from '../professions'
import PaymentCard from '../components/PaymentCard'
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { detectTimeZone, formatCurrencyAmount, getCurrencyForCountry, normalizeCountryCode } from '../countryRules'

type Role = 'patient' | 'specialist' | undefined

type Appt = {
  offerId?: string
  selectionId?: string
  specialistId?: string
  profession?: ProviderProfession
  specialty?: string
  chosenSlotISO: string
  feeCents?: number
  status?: string
  reason?: string
  createdAt?: string
  selectionCreatedAt?: string
  acceptedAt?: string
  patientName?: string
  specialistName?: string
  patient?: {
    userId?: string
    id?: string
    name?: string
    email?: string
    phone?: string
    gender?: string
    dob?: string
    medicareOrIhi?: string
    address?: { street?: string; state?: string; postalCode?: string }
  }
  meta?: Record<string, any>
  specialist?: {
    userId?: string
    name?: string
    email?: string
    phone?: string
    practiceName?: string
    displayProfession?: string
    profession?: string
    publicContact?: {
      name?: string
      email?: string
      phone?: string
      address?: { street?: string; state?: string; postalCode?: string; postcode?: string; postCode?: string }
    }
    pricing?: {
      minAud?: number
      maxAud?: number
      medicareRebate?: boolean
      notes?: string | null
    }
  }
  review?: {
    specialist?: {
      canReview?: boolean
      submitted?: boolean
      rating?: number | null
      comment?: string | null
      createdAt?: string | null
      reviewerFirstName?: string | null
      providerReply?: {
        comment?: string | null
        createdAt?: string | null
        updatedAt?: string | null
        replierName?: string | null
      } | null
    }
    client?: {
      canReview?: boolean
      submitted?: boolean
      rating?: number | null
      comment?: string | null
      createdAt?: string | null
      reviewerFirstName?: string | null
    }
  }
}

const RAW_PROVIDER_STACKS: Array<{
  id: ProviderProfession
  label: string
  fetchProfile: () => Promise<any>
}> = [
  { id: 'doctor', label: 'Medical Doctors', fetchProfile: doctorMe },
  { id: 'tech', label: 'Tech, Creative & Media', fetchProfile: techMe },
  { id: 'property', label: 'Property, Design & Building', fetchProfile: propertyMe },
  { id: 'financial', label: 'Financial Advisory', fetchProfile: financialMe },
  { id: 'allied', label: 'Allied Health', fetchProfile: alliedMe },
  { id: 'legal', label: 'Legal, Governance & Compliance', fetchProfile: legalMe },
  { id: 'migration', label: 'Migration & Visa', fetchProfile: migrationMe },
  { id: 'education', label: 'Education & Tutoring', fetchProfile: educationMe },
]
const PROVIDER_STACKS = (() => {
  const filtered = RAW_PROVIDER_STACKS.filter(stack => enabledStacks.includes(stack.id))
  return filtered.length ? filtered : [RAW_PROVIDER_STACKS[0]]
})()

const PATIENT_RESCHEDULE_FEE_CENTS = 200
const PATIENT_REBOOK_FEE_CENTS = 200

type StackStatus = 'unknown' | 'approved' | 'unapproved'

const hasStackProfile = (profile?: any) => {
  if (!profile || profile.needsRegistration) return false
  const status = String(profile.status || profile.approvalStatus || '').trim().toUpperCase()
  return status === 'APPROVED'
}

function groupByDate(appts: Appt[]) {
  const sortSlotMs = (iso: string) =>
    iso === 'REQUEST_BOOKING' ? Number.POSITIVE_INFINITY : Date.parse(iso)
  const map = new Map<string, Appt[]>()
  for (const a of appts) {
    const key =
      a.chosenSlotISO === 'REQUEST_BOOKING'
        ? 'no-slot'
        : new Date(a.chosenSlotISO).toISOString().slice(0, 10) // YYYY-MM-DD
    if (!map.has(key)) map.set(key, [])
    map.get(key)!.push(a)
  }
  const entries = [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  for (const [, arr] of entries) {
    arr.sort((x, y) => sortSlotMs(x.chosenSlotISO) - sortSlotMs(y.chosenSlotISO))
  }
  return entries
}

function money(cents?: number) {
  if (cents == null) return '—'
  return `$${(cents / 100).toFixed(2)}`
}

const normalizeCurrencyCode = (value: unknown): string | null => {
  const raw = String(value || '').trim().toUpperCase()
  return /^[A-Z]{3}$/.test(raw) ? raw : null
}

const inferCurrencyCode = (record: any, fallbackCurrency = 'AUD'): string => {
  const explicit =
    normalizeCurrencyCode(record?.currency) ||
    normalizeCurrencyCode(record?.pricing?.currency) ||
    normalizeCurrencyCode(record?.specialist?.pricing?.currency)
  if (explicit) return explicit
  const rawCountry =
    record?.address?.countryCode ||
    record?.address?.country ||
    record?.specialist?.address?.countryCode ||
    record?.specialist?.address?.country ||
    record?.patient?.address?.countryCode ||
    record?.patient?.address?.country ||
    null
  if (rawCountry) return getCurrencyForCountry(normalizeCountryCode(rawCountry))
  return normalizeCurrencyCode(fallbackCurrency) || 'AUD'
}

const feeLabel = (feeCents: number, currencyCode: string) =>
  formatCurrencyAmount(feeCents / 100, currencyCode)

function fmtDateTimeLocal(iso?: string, timeZone: string = detectTimeZone()) {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('en-AU', {
      timeZone,
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return new Date(iso).toLocaleString()
  }
}

const getProviderTitleParts = (a: Appt) => {
  const name =
    a.specialist?.name ||
    a.specialist?.publicContact?.name ||
    a.specialistName ||
    'Professional'
  const label =
    a.specialist?.displayProfession ||
    a.specialist?.profession ||
    a.specialty ||
    ''
  return { name, label }
}
function formatAppointmentTime(
  slotISO?: string,
  createdAt?: string,
  selectionCreatedAt?: string,
  timeZone: string = detectTimeZone()
) {
  if (slotISO === 'REQUEST_BOOKING') {
    const submittedAt = createdAt || selectionCreatedAt
    return `Request submitted: ${fmtDateTimeLocal(submittedAt, timeZone)} (${timeZone})`
  }
  return `Appointment time: ${fmtDateTimeLocal(slotISO, timeZone)} (${timeZone})`
}
function appointmentTimeLabel(slotISO?: string) {
  return slotISO === 'REQUEST_BOOKING' ? 'Request submitted' : 'Appointment time'
}

function Address({ a }: { a?: { street?: string; state?: string; postalCode?: string } }) {
  if (!a) return <>—</>
  const s = [a.street, a.state, a.postalCode].filter(Boolean).join(', ')
  return <>{s || '—'}</>
}
const locationOnly = (a?: { state?: string; postalCode?: string }) => {
  if (!a) return '—'
  const s = [a.state, a.postalCode].filter(Boolean).join(' ')
  return s.trim() || '—'
}
const DEFAULT_SLOT_MIN = 30 // used when backend doesn't specify appointment length
const formatProviderName = (spec?: Appt['specialist']) => {
  if (!spec) return '—'
  if (spec.practiceName) return `${spec.practiceName}${spec.name ? ` (${spec.name})` : ''}`
  return spec.name || '—'
}
const normalizeEmail = (value?: string) => (value || '').trim().toLowerCase()
const normalizePhone = (value?: string) => (value || '').replace(/[^0-9+]/g, '')
const getClientKey = (a: Appt) => {
  const meta = (a.meta || {}) as Record<string, any>
  const rawId =
    meta.patientId ||
    meta.patientUserId ||
    meta.clientId ||
    (a as any).patientId ||
    (a as any).patientUserId ||
    a.patient?.userId ||
    (a.patient as any)?.id
  if (rawId) return `id:${rawId}`
  const email = normalizeEmail(a.patient?.email)
  const phone = normalizePhone(a.patient?.phone)
  if (email && phone) return `ep:${email}|${phone}`
  if (email) return `e:${email}`
  if (phone) return `p:${phone}`
  return null
}

function getApptKey(a: Appt) {
  return a.selectionId || a.offerId || a.chosenSlotISO
}

function dedupeAppointments(list: Appt[]) {
  const seen = new Set<string>()
  const out: Appt[] = []
  for (const appt of list) {
    const keyId = getApptKey(appt)
    if (!keyId) continue
    const key = `${appt.profession || 'doctor'}:${keyId}`
    if (seen.has(key)) continue
    seen.add(key)
    out.push(appt)
  }
  return out
}

const appointmentStatusRank = (value?: string) => {
  const status = String(value || '').trim().toUpperCase()
  if (status === 'ACCEPTED' || status === 'CONFIRMED' || status === 'COMPLETED' || status === 'RESCHEDULED') return 5
  if (status === 'PENDING') return 4
  if (status === 'OPEN') return 3
  if (status === 'CANCELLED') return 2
  if (status === 'DECLINED' || status === 'LOST') return 1
  return 0
}

const normalizeSelectionAppointmentStatus = (value?: string, fallback = 'PENDING') => {
  const status = String(value || '').trim().toUpperCase()
  if (!status) return fallback
  if (status === 'DECLINED' || status === 'LOST') return ''
  if (status === 'ACCEPTED' || status === 'CONFIRMED' || status === 'COMPLETED' || status === 'RESCHEDULED') return status
  if (status === 'CANCELLED') return 'CANCELLED'
  return 'PENDING'
}

const toNumberOrUndefined = (value: unknown): number | undefined => {
  const num = Number(value)
  return Number.isFinite(num) ? num : undefined
}

const deriveAppointmentsFromSelections = (
  profession: ProviderProfession,
  payload: any
): Appt[] => {
  const items = Array.isArray(payload?.items) ? payload.items : []
  const out: Appt[] = []

  for (const selection of items) {
    const selectionId = String(selection?.selectionId || '').trim()
    if (!selectionId) continue

    const createdAt = String(selection?.createdAt || selection?.updatedAt || '').trim() || undefined
    const selectionSpecialty = String(selection?.specialty || '').trim()
    const selectionReason = String(selection?.reason || '').trim()

    const booking = selection?.booking && typeof selection.booking === 'object' ? selection.booking : null
    const bookingSpecialistId = String(booking?.specialistId || '').trim()
    const bookingSlotISO = String(booking?.slotISO || '').trim()
    const bookingStatus = normalizeSelectionAppointmentStatus(String(booking?.status || ''), 'CONFIRMED')

    const candidateList = (Array.isArray(selection?.candidates) ? selection.candidates : [])
      .filter((row: any) => {
        const status = normalizeSelectionAppointmentStatus(String(row?.status || ''), '')
        return Boolean(status)
      })
      .sort((a: any, b: any) => {
        const rankDiff = appointmentStatusRank(String(b?.status || '')) - appointmentStatusRank(String(a?.status || ''))
        if (rankDiff !== 0) return rankDiff
        const aDecision = Date.parse(String(a?.decisionAt || a?.createdAt || createdAt || ''))
        const bDecision = Date.parse(String(b?.decisionAt || b?.createdAt || createdAt || ''))
        const aTs = Number.isFinite(aDecision) ? aDecision : 0
        const bTs = Number.isFinite(bDecision) ? bDecision : 0
        return bTs - aTs
      })

    const selectedCandidate =
      (bookingSpecialistId && candidateList.find((row: any) => String(row?.specialistId || '').trim() === bookingSpecialistId)) ||
      candidateList[0] ||
      null

    const specialistId = String(selectedCandidate?.specialistId || bookingSpecialistId || '').trim()
    if (!specialistId) continue

    const candidateStatus = normalizeSelectionAppointmentStatus(String(selectedCandidate?.status || ''), '')
    const resolvedStatus =
      bookingSpecialistId && specialistId === bookingSpecialistId && bookingStatus
        ? bookingStatus
        : (candidateStatus || bookingStatus || 'PENDING')
    if (!resolvedStatus) continue

    const chosenSlotISO =
      (bookingSpecialistId && specialistId === bookingSpecialistId ? bookingSlotISO : String(selectedCandidate?.slotISO || '').trim()) ||
      bookingSlotISO ||
      String(selectedCandidate?.slotISO || '').trim() ||
      'REQUEST_BOOKING'

    const providerName =
      String(
        selectedCandidate?.providerDisplayName ||
        selectedCandidate?.specialistName ||
        selectedCandidate?.providerName ||
        ''
      ).trim() || 'Professional'
    const providerLabel =
      String(
        selectedCandidate?.providerLabel ||
        selectionSpecialty ||
        ''
      ).trim()

    out.push({
      offerId: `selection:${selectionId}:${specialistId}:${profession}`,
      selectionId,
      specialistId,
      profession,
      specialty: selectionSpecialty || providerLabel || undefined,
      chosenSlotISO,
      feeCents:
        toNumberOrUndefined(selectedCandidate?.feeCents) ??
        toNumberOrUndefined(booking?.feeCents),
      status: resolvedStatus,
      reason: selectionReason || undefined,
      createdAt,
      selectionCreatedAt: createdAt,
      acceptedAt: String(selectedCandidate?.decisionAt || booking?.createdAt || createdAt || '').trim() || undefined,
      specialistName: providerName,
      specialist: {
        userId: specialistId,
        name: String(selectedCandidate?.providerName || selectedCandidate?.specialistName || providerName || '').trim() || providerName,
        practiceName: String(selectedCandidate?.providerPractice || '').trim() || undefined,
        displayProfession: providerLabel || undefined,
        profession: providerLabel || undefined,
      },
    })
  }

  return out
}

const formatReviewDate = (iso?: string | null, timeZone: string = detectTimeZone()) => {
  if (!iso) return ''
  const trimmed = String(iso).trim()
  if (!trimmed) return ''
  let parsed: Date
  if (/^\d+$/.test(trimmed)) {
    const raw = Number(trimmed)
    const ms = trimmed.length <= 10 ? raw * 1000 : raw
    parsed = new Date(ms)
  } else {
    parsed = new Date(trimmed)
  }
  if (Number.isNaN(parsed.getTime())) return ''
  return parsed.toLocaleDateString('en-AU', {
    timeZone,
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

export default function AppointmentsPage({ role }: { role: Role }) {
  const [loading, setLoading] = React.useState(true)
  const [items, setItems] = React.useState<Appt[]>([])
  const [error, setError] = React.useState<string | null>(null)
  const [cancelBusy, setCancelBusy] = React.useState(false)
  const [rescheduleSheetOpen, setRescheduleSheetOpen] = React.useState(false)
  const [reschedulePaymentOpen, setReschedulePaymentOpen] = React.useState(false)
  const [pendingSlotISO, setPendingSlotISO] = React.useState<string | null>(null)
  const [rescheduleBusy, setRescheduleBusy] = React.useState(false)
  const [rescheduleApiError, setRescheduleApiError] = React.useState<string | null>(null)
  const [rebookSheetOpen, setRebookSheetOpen] = React.useState(false)
  const [rebookPaymentOpen, setRebookPaymentOpen] = React.useState(false)
  const [pendingRebookSlotISO, setPendingRebookSlotISO] = React.useState<string | null>(null)
  const [rebookReason, setRebookReason] = React.useState('')
  const [rebookBusy, setRebookBusy] = React.useState(false)
  const [rebookApiError, setRebookApiError] = React.useState<string | null>(null)
  const [detailNotice, setDetailNotice] = React.useState<string | null>(null)
  const [detailError, setDetailError] = React.useState<string | null>(null)
  const [specialistReviewDraft, setSpecialistReviewDraft] = React.useState({ rating: 0, comment: '' })
  const [clientReviewDraft, setClientReviewDraft] = React.useState({ rating: 0 })
  const [providerReplyDraft, setProviderReplyDraft] = React.useState('')
  const [replyBusy, setReplyBusy] = React.useState(false)
  const [replyError, setReplyError] = React.useState<string | null>(null)
  const [replyNotice, setReplyNotice] = React.useState<string | null>(null)
  const [reviewBusy, setReviewBusy] = React.useState(false)
  const [reviewError, setReviewError] = React.useState<string | null>(null)
  const [reviewNotice, setReviewNotice] = React.useState<string | null>(null)
  const [viewerCurrency, setViewerCurrency] = React.useState<string>('AUD')
  const [chatUnreadSignals, setChatUnreadSignals] = React.useState<Record<string, number>>({})
  const localTimeZone = React.useMemo(() => detectTimeZone(), [])
  const location = useLocation()
  const nav = useNavigate()
  const { appointmentId } = useParams<{ appointmentId?: string }>()
  const params = new URLSearchParams(location.search)
  const queryTarget = params.get('apt')
  const forcedView = params.get('view')
  const normalizedView =
    forcedView && forcedView.toLowerCase() === 'patient' ? 'client' : forcedView
  const stackParam = params.get('stack')
  const providerView = normalizedView === 'provider' || normalizedView === 'specialist'
  const resolvedRole: Role =
    providerView
      ? 'specialist'
      : normalizedView === 'client'
        ? 'patient'
        : role
  const isProviderView = resolvedRole === 'specialist'
  const defaultStack = (PROVIDER_STACKS[0]?.id || 'doctor') as ProviderProfession
  const resolvedStackParam = stackParam ? resolveProfessionParam(stackParam) : defaultStack
  const initialStack = PROVIDER_STACKS.find((s) => s.id === resolvedStackParam)
    ? resolvedStackParam
    : defaultStack
  const [stackProfiles, setStackProfiles] = React.useState<Record<string, any>>({})
  const [stackStatuses, setStackStatuses] = React.useState<Record<string, StackStatus>>({})
  const [activeStack, setActiveStack] = React.useState<ProviderProfession>(initialStack)
  const availableStacks = React.useMemo(
    () => PROVIDER_STACKS.filter((stack) => stackStatuses[stack.id] !== 'unapproved'),
    [stackStatuses]
  )
  const activeStackMeta = React.useMemo(
    () =>
      availableStacks.find(stack => stack.id === activeStack) ||
      availableStacks[0] ||
      PROVIDER_STACKS[0],
    [activeStack, availableStacks]
  )
  const viewParam =
    providerView
      ? 'provider'
      : resolvedRole === 'patient'
        ? 'client'
        : undefined
  const viewSearch = React.useMemo(() => {
    if (!viewParam) return ''
    const next = new URLSearchParams()
    next.set('view', viewParam)
    return `?${next.toString()}`
  }, [viewParam])
  const { refresh: refreshNotifications } = useNotifications()
  const reviewSectionRef = React.useRef<HTMLDivElement | null>(null)
  const focusReview =
    params.get('review') === '1' ||
    params.get('focus') === 'review' ||
    location.hash === '#review'
  const slotMs = (iso: string) =>
    iso === 'REQUEST_BOOKING' ? Number.POSITIVE_INFINITY : Date.parse(iso)
  const listTimeMs = (a: Appt) => {
    if (a.chosenSlotISO && a.chosenSlotISO !== 'REQUEST_BOOKING') {
      const ts = Date.parse(a.chosenSlotISO)
      if (Number.isFinite(ts)) return ts
    }
    const submitted = a.selectionCreatedAt || a.createdAt
    if (!submitted) return 0
    const ts = Date.parse(submitted)
    return Number.isFinite(ts) ? ts : 0
  }
  const slotElapsed = (a: Appt) => {
    const start = slotMs(a.chosenSlotISO)
    return Number.isFinite(start) && Date.now() >= start + DEFAULT_SLOT_MIN * 60000
  }
  const displayStatus = (a: Appt) => {
    const raw = (a.status || '').trim().toUpperCase()
    if (raw === 'CANCELLED') return 'Cancelled'
    if (raw === 'RESCHEDULED') return 'Rescheduled'
    if (slotElapsed(a)) return 'Completed'
    if (raw === 'PENDING') return 'Pending'
    if (raw === 'ACCEPTED' || raw === 'CONFIRMED') return 'Confirmed'
    return 'Confirmed'
  }
  const reviewReady = (a: Appt) => {
    const raw = (a.status || '').trim().toUpperCase()
    if (raw === 'CANCELLED' || raw === 'PENDING') return false
    if (raw === 'ACCEPTED' || raw === 'CONFIRMED' || raw === 'COMPLETED') return true
    return slotElapsed(a)
  }
  const apptTarget = appointmentId || queryTarget || null
  const active = React.useMemo(() => {
    if (!apptTarget) return null
    return items.find(
      (a) =>
        a.offerId === apptTarget ||
        a.selectionId === apptTarget ||
        a.chosenSlotISO === apptTarget
    ) || null
  }, [items, apptTarget])
  const encounterOptions = React.useMemo(() => {
    if (!active || resolvedRole !== 'specialist') return []
    const key = getClientKey(active)
    if (!key) return []
    return items
      .filter((a) => getClientKey(a) === key)
      .filter((a) => {
        const label = displayStatus(a)
        return label !== 'Pending' && label !== 'Cancelled'
      })
      .map((a) => {
        const id = getApptKey(a)
        if (!id) return null
        const rawTime = formatAppointmentTime(a.chosenSlotISO, a.createdAt, a.selectionCreatedAt, localTimeZone)
        const timeLabel = rawTime.replace(/^Request submitted:\s*/i, '').trim()
        return {
          id,
          label: `${timeLabel}${a.chosenSlotISO === 'REQUEST_BOOKING' ? ' · Submitted' : ''} · ${displayStatus(a)}`,
          sortKey: listTimeMs(a),
        }
      })
      .filter((entry): entry is { id: string; label: string; sortKey: number } => Boolean(entry))
      .sort((a, b) => b.sortKey - a.sortKey)
  }, [active, resolvedRole, items, listTimeMs, displayStatus])
  const activeEncounterId = active ? getApptKey(active) : null
  const activeStatusLabel = active ? displayStatus(active) : ''
  const hasActiveEncounter = activeEncounterId
    ? encounterOptions.some((entry) => entry.id === activeEncounterId)
    : false
  const showEncounterSwitch =
    resolvedRole === 'specialist' &&
    active &&
    ['Confirmed', 'Completed', 'Rescheduled'].includes(activeStatusLabel) &&
    encounterOptions.length > 1 &&
    hasActiveEncounter
  const patientBasePath = location.pathname.startsWith('/clientdashboard')
    ? '/clientdashboard'
    : '/appointments'
  const listHref = React.useMemo(() => {
    const params = new URLSearchParams()
    if (viewParam) params.set('view', viewParam)
    if (resolvedRole === 'specialist') params.set('stack', activeStack)
    const search = params.toString()
    const basePath = resolvedRole === 'patient' ? patientBasePath : '/appointments'
    return search ? `${basePath}?${search}` : basePath
  }, [viewParam, resolvedRole, activeStack, patientBasePath])
  const detailHref = React.useCallback(
    (id: string, stackOverride?: ProviderProfession) => {
      const params = new URLSearchParams()
      if (viewParam) params.set('view', viewParam)
      const stackValue = stackOverride || (resolvedRole === 'specialist' ? activeStack : undefined)
      if (stackValue) params.set('stack', stackValue)
      const search = params.toString()
      const basePath = resolvedRole === 'patient' ? patientBasePath : '/appointments'
      return `${basePath}/${encodeURIComponent(id)}${search ? `?${search}` : ''}`
    },
    [viewParam, resolvedRole, activeStack, patientBasePath]
  )
  const setSearchParams = React.useCallback(
    (updates: Record<string, string | null>) => {
      const next = new URLSearchParams(location.search)
      Object.entries(updates).forEach(([key, value]) => {
        if (value == null) next.delete(key)
        else next.set(key, value)
      })
      const search = next.toString()
      nav({ pathname: location.pathname, search: search ? `?${search}` : '' }, { replace: true })
    },
    [location.pathname, location.search, nav]
  )
  const fromList = Boolean((location.state as any)?.fromAppointmentsList)
  React.useEffect(() => {
    if (!active || resolvedRole !== 'specialist') return
    if (active.profession && active.profession !== activeStack) {
      setActiveStack(active.profession)
      setSearchParams({ stack: active.profession })
    }
  }, [active, resolvedRole, activeStack, setSearchParams])

  React.useEffect(() => {
    let cancelled = false
    api
      .me()
      .then((me: any) => {
        if (cancelled) return
        const currency =
          normalizeCurrencyCode(me?.currency) ||
          getCurrencyForCountry(
            normalizeCountryCode(
              me?.address?.countryCode || me?.address?.country || me?.countryCode || me?.country || 'AU'
            )
          )
        setViewerCurrency(currency || 'AUD')
      })
      .catch(() => {
        if (!cancelled) setViewerCurrency('AUD')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const handleStackSwitch = (stackId: ProviderProfession) => {
    if (stackId === activeStack) return
    setActiveStack(stackId)
    setSearchParams({ stack: stackId })
  }
  const renderStackSwitcher = () => {
    // Hide stack chips on appointment detail; only show on the list view.
    if (!isProviderView || active || availableStacks.length <= 1) return null
    return (
      <div className="chip-row" role="tablist" style={{ marginBottom: '1rem', flexWrap: 'wrap' }}>
        {availableStacks.map(stack => (
          <button
            key={stack.id}
            type="button"
            className={`chip ${stack.id === activeStack ? 'active' : ''}`}
            onClick={() => handleStackSwitch(stack.id)}
          >
            {stack.label}
          </button>
        ))}
      </div>
    )
  }

  const fetchAppointments = React.useCallback(
    async (opts: { withSpinner?: boolean } = {}) => {
      if (opts.withSpinner) setLoading(true)
      try {
        let appts: Appt[] = []
        if (resolvedRole === 'patient') {
          const stacks = (enabledStacks.length ? enabledStacks : ['doctor']) as ProfessionKey[]
          for (const stack of stacks) {
            const stackRows: Appt[] = []
            try {
              const res = await api.getStackAppointments(stack, 'patient')
              const list = Array.isArray(res?.appointments) ? res.appointments : []
              stackRows.push(...list.map((appt: Appt) => ({ ...appt, profession: stack })))
            } catch (err: any) {
              const msg = String(err?.message || '')
              if (!msg.toLowerCase().includes('forbidden') && !msg.includes('tableName')) {
                console.error('fetchAppointments patient stack failed', stack, err)
              }
            }
            try {
              const selectionsRes = await api.selectionsMine(stack)
              const derived = deriveAppointmentsFromSelections(stack, selectionsRes)
              stackRows.push(...derived)
            } catch (err: any) {
              const msg = String(err?.message || '')
              if (
                !msg.toLowerCase().includes('forbidden') &&
                !msg.toLowerCase().includes('unauthorized') &&
                !msg.toLowerCase().includes('not found')
              ) {
                console.warn('fetchAppointments patient selections fallback failed', stack, err)
              }
            }
            appts.push(...stackRows)
          }
          if (!appts.length) {
            try {
              const fallback = await api.getAppointments('patient')
              const list = Array.isArray(fallback?.appointments) ? fallback.appointments : []
              appts.push(...list.map((appt: Appt) => ({ ...appt, profession: appt.profession || 'doctor' })))
            } catch (fallbackErr) {
              console.warn('fallback patient appointments failed', fallbackErr)
            }
          }
        } else {
          try {
            const res = await api.getStackAppointments(activeStack, 'specialist')
            const list = Array.isArray(res?.appointments) ? res.appointments : []
            appts = list.map((appt: Appt) => ({ ...appt, profession: activeStack }))
          } catch (err: any) {
            const msg = String(err?.message || '')
            if (msg.includes('tableName') || msg.toLowerCase().includes('forbidden')) {
              try {
                const fallback = await api.getAppointments('specialist')
                const list = Array.isArray(fallback?.appointments) ? fallback.appointments : []
                appts = list.map((appt: Appt) => ({ ...appt, profession: appt.profession || activeStack }))
              } catch (fallbackErr) {
                throw fallbackErr
              }
            } else {
              throw err
            }
          }
        }
        const unique = dedupeAppointments(appts)
        const now = Date.now()
        const upcoming = unique
          .filter(a => slotMs(a.chosenSlotISO) >= now)
          .sort((a, b) => slotMs(a.chosenSlotISO) - slotMs(b.chosenSlotISO))
        const past = unique
          .filter(a => slotMs(a.chosenSlotISO) < now)
          .sort((a, b) => slotMs(b.chosenSlotISO) - slotMs(a.chosenSlotISO))
        setItems([...upcoming, ...past])
      } catch (e: any) {
        setError(e?.message || 'Failed to load appointments.')
      } finally {
        if (opts.withSpinner) setLoading(false)
      }
    },
    [resolvedRole, activeStack]
  )
  const loadUnreadBySelection = React.useCallback(async () => {
    const stacksToLoad: ProviderProfession[] = resolvedRole === 'patient'
      ? (enabledStacks.length ? enabledStacks : ['doctor'])
      : [activeStack]
    const uniqueStacks = Array.from(new Set(stacksToLoad))
    const next: Record<string, number> = {}
    const bump = (key: string, unread: number) => {
      if (!key) return
      if (!Number.isFinite(unread) || unread <= 0) return
      next[key] = (next[key] || 0) + Math.floor(unread)
    }
    await Promise.all(uniqueStacks.map(async (stack) => {
      try {
        let cursor: string | null = null
        let pages = 0
        do {
          const res = await atlas.chatListConversations(stack, {
            limit: 100,
            cursor: cursor || undefined,
          })
          const rows = Array.isArray(res?.items) ? res.items : []
          for (const row of rows) {
            const unread = Number(row?.unreadCount || 0)
            if (!Number.isFinite(unread) || unread <= 0) continue
            const selectionId = String(row?.selectionId || '').trim()
            const patientId = String(row?.patientId || '').trim()
            const specialistId = String(row?.specialistId || '').trim()
            const participantRole = String(row?.participantRole || '').trim().toLowerCase()
            const peerId = String(row?.peerId || '').trim()
            bump(selectionId ? `selection:${selectionId}` : '', unread)
            bump(patientId ? `patient:${patientId}` : '', unread)
            bump(specialistId ? `specialist:${specialistId}` : '', unread)
            if (participantRole === 'specialist') {
              bump(peerId ? `patient:${peerId}` : '', unread)
            } else if (participantRole === 'patient') {
              bump(peerId ? `specialist:${peerId}` : '', unread)
            }
          }
          cursor = String(res?.nextCursor || '').trim() || null
          pages += 1
        } while (cursor && pages < 8)
      } catch {
        // Keep dashboard usable even when one stack does not expose chat yet.
      }
    }))
    return next
  }, [resolvedRole, activeStack])
  const appointmentCurrencyCode = React.useMemo(
    () => inferCurrencyCode(active, viewerCurrency),
    [active, viewerCurrency]
  )
  const rescheduleFeeText = feeLabel(PATIENT_RESCHEDULE_FEE_CENTS, appointmentCurrencyCode)
  const rebookFeeText = feeLabel(PATIENT_REBOOK_FEE_CENTS, appointmentCurrencyCode)

  React.useEffect(() => {
    fetchAppointments({ withSpinner: true })
  }, [fetchAppointments])

  React.useEffect(() => {
    let cancelled = false
    const syncUnread = async () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
      const next = await loadUnreadBySelection()
      if (!cancelled) setChatUnreadSignals(next)
    }
    void syncUnread()
    if (typeof window === 'undefined') return () => { cancelled = true }
    const timer = window.setInterval(() => {
      void syncUnread()
    }, 20000)
    return () => {
      cancelled = true
      window.clearInterval(timer)
    }
  }, [loadUnreadBySelection])

  React.useEffect(() => {
    if (!isProviderView) return
    let cancelled = false
    ;(async () => {
      const updatesStatus: Record<string, StackStatus> = {}
      const updatesProfiles: Record<string, any> = {}
      for (const stack of PROVIDER_STACKS) {
        try {
          const profile = await stack.fetchProfile().catch(() => ({ needsRegistration: true }))
          if (cancelled) return
          const approved = hasStackProfile(profile)
          updatesStatus[stack.id] = approved ? 'approved' : 'unapproved'
          updatesProfiles[stack.id] = profile
        } catch (err: any) {
          if (cancelled) return
          const msg = String(err?.message || '').toLowerCase()
          const isAuth = msg.includes('forbidden') || msg.includes('unauthorized') || msg.includes('403')
          updatesStatus[stack.id] = isAuth ? 'unapproved' : (stackStatuses[stack.id] || 'unknown')
        }
      }
      if (cancelled) return
      if (Object.keys(updatesProfiles).length) {
        setStackProfiles(prev => ({ ...prev, ...updatesProfiles }))
      }
      if (Object.keys(updatesStatus).length) {
        setStackStatuses(prev => ({ ...prev, ...updatesStatus }))
      }
    })()
    return () => { cancelled = true }
  }, [isProviderView])

  React.useEffect(() => {
    if (!stackParam) return
    const next = resolveProfessionParam(stackParam)
    if (stackStatuses[next] === 'unapproved') return
    if (next !== activeStack) setActiveStack(next)
  }, [stackParam, activeStack, stackStatuses])

  React.useEffect(() => {
    if (!isProviderView) return
    if (!stackParam) {
      setSearchParams({ stack: activeStack })
    }
  }, [isProviderView, stackParam, activeStack, setSearchParams])

  React.useEffect(() => {
    if (!isProviderView) return
    if (availableStacks.length && !availableStacks.some(s => s.id === activeStack)) {
      setActiveStack(availableStacks[0].id)
    }
  }, [isProviderView, availableStacks, activeStack])

  const loadStackProfile = React.useCallback(
    async (stackId: ProviderProfession) => {
      if (stackProfiles[stackId]) return
      const meta = PROVIDER_STACKS.find((s) => s.id === stackId)
      if (!meta) return
      try {
        const profile = await meta.fetchProfile().catch(() => null)
        const safeProfile = profile && hasStackProfile(profile) ? profile : { needsRegistration: true }
        setStackProfiles((prev) => ({ ...prev, [stackId]: safeProfile }))
      } catch {
        setStackProfiles((prev) => ({ ...prev, [stackId]: { needsRegistration: true } }))
      }
    },
    [stackProfiles]
  )

  React.useEffect(() => {
    if (!isProviderView) return
    loadStackProfile(activeStackMeta.id)
  }, [isProviderView, activeStackMeta.id, loadStackProfile])

  React.useEffect(() => {
    setSpecialistReviewDraft({ rating: 0, comment: '' })
    setClientReviewDraft({ rating: 0 })
    setProviderReplyDraft(String(active?.review?.specialist?.providerReply?.comment || ''))
    setReplyError(null)
    setReplyNotice(null)
    setReviewError(null)
    setReviewNotice(null)
  }, [active?.selectionId, active?.review?.specialist?.providerReply?.comment, resolvedRole])

  React.useEffect(() => {
    setDetailNotice(null)
    setDetailError(null)
    setRescheduleSheetOpen(false)
    setReschedulePaymentOpen(false)
    setPendingSlotISO(null)
    setRescheduleApiError(null)
    setRescheduleBusy(false)
    setRebookSheetOpen(false)
    setRebookPaymentOpen(false)
    setPendingRebookSlotISO(null)
    setRebookReason('')
    setRebookApiError(null)
    setRebookBusy(false)
  }, [active?.selectionId])

  React.useEffect(() => {
    if (!focusReview || !active) return
    reviewSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [focusReview, active])

  React.useEffect(() => {
    if (!queryTarget || appointmentId) return
    nav(detailHref(queryTarget), { replace: true })
  }, [appointmentId, queryTarget, detailHref, nav])

  const appendReviewParam = React.useCallback((href: string) => {
    const [path, hash] = href.split('#')
    const sep = path.includes('?') ? '&' : '?'
    return `${path}${sep}review=1${hash ? `#${hash}` : ''}`
  }, [])

  const openDetail = React.useCallback(
    (appt: Appt, opts?: { focusReview?: boolean }) => {
      const key = getApptKey(appt)
      if (!key) return
      const base = detailHref(key)
      const target = opts?.focusReview ? appendReviewParam(base) : base
      nav(target, { state: { fromAppointmentsList: true } })
    },
    [detailHref, nav, appendReviewParam]
  )
  const closeDetail = React.useCallback(() => {
    if (fromList) {
      nav(-1)
    } else {
      nav(listHref, { replace: true })
    }
  }, [fromList, listHref, nav])

  // Show cancel if backend method for the corresponding pathway exists.
  const canCancel = (a: Appt) => {
    if (a.offerId && typeof (api as any).cancelAppointment === 'function') return true
    if (a.selectionId && typeof (api as any).cancelSelection === 'function') return true
    return false
  }

  const cancelAppt = async (a: Appt) => {
    if (!canCancel(a)) return
    if (!confirm('Cancel this appointment?')) return
    setCancelBusy(true)
    try {
      if (a.offerId && (api as any).cancelAppointment) {
        await (api as any).cancelAppointment(a.offerId)
        setItems(prev => prev.filter(x => x.offerId !== a.offerId))
      } else if (a.selectionId) {
        const stack = (a.profession as ProviderProfession) || 'doctor'
        await atlas.cancelSelection(stack, a.selectionId)
        setItems(prev => prev.filter(x => x.selectionId !== a.selectionId))
      }
      closeDetail()
      nav(listHref, { replace: true })
    } catch (e: any) {
      alert(e?.message || 'Failed to cancel appointment.')
    } finally {
      setCancelBusy(false)
    }
  }

  const handleRescheduleSlotPick = React.useCallback(
    (slotISO: string) => {
      setPendingSlotISO(slotISO)
      setRescheduleSheetOpen(false)
      setReschedulePaymentOpen(true)
      setRescheduleApiError(null)
    },
    []
  )

  const handleReschedulePayment = React.useCallback(
    async (paymentMethodId: string) => {
      if (!active || resolvedRole !== 'patient' || !active.selectionId || !pendingSlotISO) return
      const profession: ProviderProfession = (active.profession as ProviderProfession) || 'doctor'
      setRescheduleBusy(true)
      setRescheduleApiError(null)
      setDetailError(null)
      setDetailNotice(null)
      try {
        await atlas.rescheduleSelection(profession, active.selectionId, {
          slotISO: pendingSlotISO,
          paymentMethodId,
        })
        await fetchAppointments()
        refreshNotifications()
        const label = fmtDateTimeLocal(pendingSlotISO, localTimeZone)
        setDetailNotice(`Appointment moved to ${label}.`)
        setPendingSlotISO(null)
        setReschedulePaymentOpen(false)
      } catch (err: any) {
        setRescheduleApiError(err?.message || 'Failed to reschedule.')
      } finally {
        setRescheduleBusy(false)
      }
    },
    [active, resolvedRole, pendingSlotISO, fetchAppointments, refreshNotifications, localTimeZone]
  )

  const handleRebookSlotPick = React.useCallback(
    (slotISO: string) => {
      setPendingRebookSlotISO(slotISO)
      setRebookSheetOpen(false)
      setRebookPaymentOpen(true)
      setRebookReason('')
      setRebookApiError(null)
    },
    []
  )

  const handleRebookPayment = React.useCallback(
    async (paymentMethodId: string, reasonText: string) => {
      if (
        !active ||
        resolvedRole !== 'patient' ||
        !active.selectionId ||
        !pendingRebookSlotISO
      )
        return
      const profession: ProviderProfession = (active.profession as ProviderProfession) || 'doctor'
      setRebookBusy(true)
      setRebookApiError(null)
      setDetailError(null)
      setDetailNotice(null)
      try {
        await atlas.rebookSelection(profession, active.selectionId, {
          slotISO: pendingRebookSlotISO,
          paymentMethodId,
          reason: reasonText.trim(),
        })
        await fetchAppointments()
        refreshNotifications()
        setDetailNotice('Follow-up request sent. We’ll notify you once the provider responds.')
        setPendingRebookSlotISO(null)
        setRebookPaymentOpen(false)
      } catch (err: any) {
        setRebookApiError(err?.message || 'Failed to rebook.')
      } finally {
        setRebookBusy(false)
      }
    },
    [active, resolvedRole, pendingRebookSlotISO, fetchAppointments, refreshNotifications]
  )

  const canReschedule = React.useMemo(() => {
    if (resolvedRole !== 'patient' || !active) return false
    if (!active.selectionId) return false
    const specialistId = active.specialist?.userId || active.specialistId
    if (!specialistId) return false
    const slotStart = slotMs(active.chosenSlotISO)
    return Number.isFinite(slotStart) && slotStart > Date.now()
  }, [active, resolvedRole])
  const canRebook = React.useMemo(() => {
    if (resolvedRole !== 'patient' || !active) return false
    if (!active.selectionId) return false
    const specialistId = active.specialist?.userId || active.specialistId
    if (!specialistId) return false
    return displayStatus(active) === 'Completed'
  }, [active, resolvedRole])

  const handleSubmitSpecialistReview = async () => {
    if (!active) return
    const selectionId = active.selectionId
    const specialistId = active.specialist?.userId || active.specialistId
    if (!selectionId || !specialistId) {
      setReviewError('Missing data for this review.')
      return
    }
    if (specialistReviewDraft.rating < 1) {
      setReviewError('Select a rating before submitting.')
      return
    }
    setReviewBusy(true)
    setReviewError(null)
    setReviewNotice(null)
    const reviewProfession: ProviderProfession =
      active.profession || (resolvedRole === 'specialist' ? activeStack : 'doctor')
    try {
      await api.submitSpecialistReview(reviewProfession, {
        selectionId,
        specialistId,
        rating: specialistReviewDraft.rating,
        comment: specialistReviewDraft.comment.trim() || undefined,
      })
      setReviewNotice('Thanks — your review was submitted.')
      await fetchAppointments()
      refreshNotifications()
    } catch (e: any) {
      setReviewError(e?.message || 'Failed to submit review.')
    } finally {
      setReviewBusy(false)
    }
  }

  const handleSubmitClientReview = async () => {
    if (!active) return
    const selectionId = active.selectionId
    const patientId = active.patient?.userId || (active as any).patientId
    if (!selectionId || !patientId) {
      setReviewError('Missing data for this review.')
      return
    }
    if (clientReviewDraft.rating < 1) {
      setReviewError('Select a rating before submitting.')
      return
    }
    setReviewBusy(true)
    setReviewError(null)
    setReviewNotice(null)
    const reviewProfession: ProviderProfession =
      active.profession || (resolvedRole === 'specialist' ? activeStack : 'doctor')
    try {
      await api.submitClientReview(reviewProfession, {
        selectionId,
        patientId,
        rating: clientReviewDraft.rating,
      })
      setReviewNotice('Thanks — your rating was saved.')
      await fetchAppointments()
      refreshNotifications()
    } catch (e: any) {
      setReviewError(e?.message || 'Failed to submit rating.')
    } finally {
      setReviewBusy(false)
    }
  }

  const handleSubmitProviderReply = async () => {
    if (!active) return
    const selectionId = active.selectionId
    const patientId = active.patient?.userId || (active as any).patientId
    if (!selectionId || !patientId) {
      setReplyError('Missing data for this reply.')
      return
    }
    if (!active.review?.specialist?.submitted) {
      setReplyError('Client review not found yet.')
      return
    }
    const trimmedReply = providerReplyDraft.trim()
    if (!trimmedReply) {
      setReplyError('Type a reply before posting.')
      return
    }
    setReplyBusy(true)
    setReplyError(null)
    setReplyNotice(null)
    const reviewProfession: ProviderProfession =
      active.profession || (resolvedRole === 'specialist' ? activeStack : 'doctor')
    try {
      await api.replyToSpecialistReview(reviewProfession, {
        selectionId,
        patientId,
        comment: trimmedReply,
      })
      setReplyNotice('Reply saved. It is now live on your public EUCA Match profile, and the client was notified by email.')
      await fetchAppointments()
      refreshNotifications()
    } catch (e: any) {
      setReplyError(e?.message || 'Failed to save reply.')
    } finally {
      setReplyBusy(false)
    }
  }

  const renderPatientReviews = () => {
    if (!active) return null
    const myReview = active.review?.specialist
    const providerFeedback = active.review?.client
    const providerReply = myReview?.providerReply
    const ready = reviewReady(active)
    return (
      <div className="appointment-review-stack">
        {myReview?.submitted ? (
          <div className="appointment-review-card">
            <div className="detail-label">Your review</div>
            <RatingStarsDisplay value={myReview.rating || 0} showValue />
            {myReview.comment && (
              <p className="hint" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                “{myReview.comment}”
              </p>
            )}
            <p className="hint" style={{ marginTop: 8 }}>
              Submitted {formatReviewDate(myReview.createdAt, localTimeZone)}
            </p>
            {providerReply?.comment ? (
              <div className="appointment-review-reply">
                <div className="detail-label">Professional reply</div>
                <p className="hint" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
                  “{providerReply.comment}”
                </p>
                <p className="hint" style={{ marginTop: 6 }}>
                  {providerReply.replierName || 'Professional'} replied{' '}
                  {formatReviewDate(providerReply.updatedAt || providerReply.createdAt, localTimeZone)}
                </p>
              </div>
            ) : null}
          </div>
        ) : ready ? (
          <div className="appointment-review-card">
            <div className="detail-label">Leave a review</div>
            <RatingStarsInput
              value={specialistReviewDraft.rating}
              onChange={(val) => {
                setSpecialistReviewDraft(prev => ({ ...prev, rating: val }))
                setReviewError(null)
                setReviewNotice(null)
              }}
              disabled={reviewBusy}
            />
            <textarea
              className="input"
              placeholder="Optional comments (shown to the specialist)"
              rows={3}
              style={{ marginTop: 12 }}
              value={specialistReviewDraft.comment}
              onChange={(e) => {
                setSpecialistReviewDraft(prev => ({ ...prev, comment: e.target.value }))
                setReviewError(null)
                setReviewNotice(null)
              }}
              disabled={reviewBusy}
            />
            {reviewError && (
              <div className="field-help" role="alert" style={{ marginTop: 8 }}>
                {reviewError}
              </div>
            )}
            {reviewNotice && (
              <div className="hint" style={{ marginTop: 8, color: 'var(--success)' }}>
                {reviewNotice}
              </div>
            )}
            <div className="inline-gap-2" style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleSubmitSpecialistReview}
                disabled={reviewBusy}
              >
                {reviewBusy ? 'Submitting…' : 'Submit review'}
              </button>
            </div>
          </div>
        ) : null}

        {providerFeedback?.submitted && (
          <div className="appointment-review-card muted">
            <div className="detail-label">Specialist feedback</div>
            <RatingStarsDisplay value={providerFeedback.rating || 0} showValue />
            <p className="hint" style={{ marginTop: 8 }}>
              Rated on {formatReviewDate(providerFeedback.createdAt, localTimeZone)}
            </p>
          </div>
        )}
      </div>
    )
  }

  const renderProviderReviews = () => {
    if (!active) return null
    const patientReview = active.review?.specialist
    const providerReply = patientReview?.providerReply
    const myClientReview = active.review?.client
    const ready = reviewReady(active)
    return (
      <div className="appointment-review-stack">
        {patientReview?.submitted && (
          <div className="appointment-review-card">
            <div className="detail-label">Client feedback</div>
            <div className="hint" style={{ marginBottom: 4 }}>
              {patientReview.reviewerFirstName || 'Client'}
            </div>
            <RatingStarsDisplay value={patientReview.rating || 0} showValue />
            {patientReview.comment && (
              <p className="hint" style={{ whiteSpace: 'pre-wrap', marginTop: 8 }}>
                “{patientReview.comment}”
              </p>
            )}
            <p className="hint" style={{ marginTop: 8 }}>
              Received {formatReviewDate(patientReview.createdAt, localTimeZone)}
            </p>
            <div className="appointment-review-reply">
              <div className="detail-label">{providerReply?.comment ? 'Update your reply' : 'Reply to this review'}</div>
              <p className="hint" style={{ marginTop: 4 }}>
                Your reply is shown on your public EUCA Match profile.
              </p>
              <textarea
                className="input"
                placeholder="Write a short professional reply"
                rows={3}
                style={{ marginTop: 10 }}
                value={providerReplyDraft}
              onChange={(e) => {
                setProviderReplyDraft(e.target.value)
                setReplyError(null)
                setReplyNotice(null)
              }}
                disabled={replyBusy}
              />
              {providerReply?.comment ? (
                <p className="hint" style={{ marginTop: 8 }}>
                  Last reply sent {formatReviewDate(providerReply.updatedAt || providerReply.createdAt, localTimeZone)}
                </p>
              ) : null}
              {replyError && (
                <div className="field-help" role="alert" style={{ marginTop: 8 }}>
                  {replyError}
                </div>
              )}
              {replyNotice && (
                <div className="hint" style={{ marginTop: 8, color: 'var(--success)' }}>
                  {replyNotice}
                </div>
              )}
              <div className="inline-gap-2" style={{ marginTop: 12 }}>
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={handleSubmitProviderReply}
                  disabled={replyBusy}
                >
                  {replyBusy ? 'Saving…' : providerReply?.comment ? 'Update reply' : 'Post reply'}
                </button>
              </div>
            </div>
          </div>
        )}
        {ready && !myClientReview?.submitted ? (
          <div className="appointment-review-card">
            <div className="detail-label">Rate this client</div>
            <RatingStarsInput
              value={clientReviewDraft.rating}
              onChange={(val) => {
                setClientReviewDraft({ rating: val })
                setReviewError(null)
                setReviewNotice(null)
              }}
              disabled={reviewBusy}
            />
            {reviewError && (
              <div className="field-help" role="alert" style={{ marginTop: 8 }}>
                {reviewError}
              </div>
            )}
            {reviewNotice && (
              <div className="hint" style={{ marginTop: 8, color: 'var(--success)' }}>
                {reviewNotice}
              </div>
            )}
            <div className="inline-gap-2" style={{ marginTop: 12 }}>
              <button
                className="btn btn-primary"
                type="button"
                onClick={handleSubmitClientReview}
                disabled={reviewBusy}
              >
                {reviewBusy ? 'Submitting…' : 'Submit rating'}
              </button>
            </div>
          </div>
        ) : myClientReview?.submitted ? (
          <div className="appointment-review-card">
            <div className="detail-label">Your rating</div>
            <RatingStarsDisplay value={myClientReview.rating || 0} showValue />
            <p className="hint" style={{ marginTop: 8 }}>
              Submitted {formatReviewDate(myClientReview.createdAt, localTimeZone)}
            </p>
          </div>
        ) : null}
      </div>
    )
  }

  const sorted = React.useMemo(() => {
    const arr = [...items]
    if (resolvedRole === 'patient') {
      return arr.sort((a, b) => listTimeMs(b) - listTimeMs(a))
    }
    return arr.sort((a, b) => slotMs(a.chosenSlotISO) - slotMs(b.chosenSlotISO))
  }, [items, resolvedRole])
  const filteredAppointments = sorted
  const dismissLabel = fromList ? 'Back' : 'Close'

  if (loading) return <div className="container page-wide"><Spinner label="Loading appointments…" /></div>
  if (error) return <div className="container page-wide"><div className="banner error">{error}</div></div>
  const statusClass = (a: Appt) => {
    const label = displayStatus(a)
    if (label === 'Pending') return 'status-pending'
    if (label === 'Completed') return 'status-accepted'
    if (label === 'Cancelled') return 'status-cancelled'
    if (label === 'Rescheduled') return 'status-accepted'
    return 'status-accepted'
  }
  const needsReview = (a: Appt) => {
    if (!reviewReady(a)) return false
    if (resolvedRole === 'patient') return !(a.review?.specialist?.submitted)
    if (resolvedRole === 'specialist') return !(a.review?.client?.submitted)
    return true
  }
  const isClientAccepted = (a: Appt) => {
    const label = displayStatus(a)
    return label === 'Confirmed' || label === 'Completed' || label === 'Rescheduled'
  }
  const acceptedTimeMs = (a: Appt) => {
    const acceptedRaw = String(
      a.acceptedAt ||
      a.meta?.acceptedAt ||
      a.meta?.decisionAt ||
      ''
    ).trim()
    if (acceptedRaw) {
      const acceptedTs = Date.parse(acceptedRaw)
      if (Number.isFinite(acceptedTs)) return acceptedTs
    }
    const createdRaw = String(a.createdAt || a.selectionCreatedAt || '').trim()
    if (!createdRaw) return 0
    const createdTs = Date.parse(createdRaw)
    return Number.isFinite(createdTs) ? createdTs : 0
  }
  const unreadForAppointment = (a: Appt) => {
    const selectionId = String(a.selectionId || '').trim()
    const patientId = String(a.patient?.userId || a.patient?.id || a.meta?.patientId || '').trim()
    const specialistId = String(a.specialist?.userId || a.specialistId || '').trim()
    const exact = selectionId ? Number(chatUnreadSignals[`selection:${selectionId}`] || 0) : 0
    if (Number.isFinite(exact) && exact > 0) return exact
    if (resolvedRole === 'specialist') {
      const byPatient = patientId ? Number(chatUnreadSignals[`patient:${patientId}`] || 0) : 0
      return Number.isFinite(byPatient) && byPatient > 0 ? byPatient : 0
    }
    const bySpecialist = specialistId ? Number(chatUnreadSignals[`specialist:${specialistId}`] || 0) : 0
    return Number.isFinite(bySpecialist) && bySpecialist > 0 ? bySpecialist : 0
  }

  const clearUnreadForAppointment = (a: Appt) => {
    const selectionId = String(a.selectionId || '').trim()
    const patientId = String(a.patient?.userId || a.patient?.id || a.meta?.patientId || '').trim()
    const specialistId = String(a.specialist?.userId || a.specialistId || '').trim()
    setChatUnreadSignals((prev) => {
      const next = { ...prev }
      if (selectionId) delete next[`selection:${selectionId}`]
      if (patientId) delete next[`patient:${patientId}`]
      if (specialistId) delete next[`specialist:${specialistId}`]
      return next
    })
  }
  const clientAcceptedAppointments =
    resolvedRole === 'patient'
      ? filteredAppointments
        .filter(isClientAccepted)
        .sort((a, b) => acceptedTimeMs(b) - acceptedTimeMs(a))
      : []
  const canOpenChat = (a: Appt) => {
    const selectionId = String(a.selectionId || '').trim()
    const specialistId = String(a.specialist?.userId || a.specialistId || '').trim()
    const patientId = String(a.patient?.userId || a.patient?.id || a.meta?.patientId || '').trim()
    return Boolean(selectionId || specialistId || patientId)
  }

  const openConversationChat = (a: Appt) => {
    const selectionId = String(a.selectionId || '').trim()
    const specialistId = String(a.specialist?.userId || a.specialistId || '').trim()
    const patientId = String(a.patient?.userId || a.patient?.id || a.meta?.patientId || '').trim()
    if (!selectionId && !specialistId && !patientId) {
      openDetail(a)
      return
    }
    if (typeof window === 'undefined') return
    const provider = getProviderTitleParts(a)
    const clientName = String(a.patientName || a.patient?.name || a.meta?.patientName || '').trim() || 'Client'
    const isSpecialistView = resolvedRole === 'specialist'
    window.dispatchEvent(
      new CustomEvent('chat:open', {
        detail: {
          profession: (a.profession as ProviderProfession) || 'doctor',
          selectionId: selectionId || undefined,
          specialistId: specialistId || undefined,
          patientId: patientId || undefined,
          peerName: isSpecialistView ? clientName : provider.name,
          patientName: clientName,
          minimized: false,
        },
      })
    )
    clearUnreadForAppointment(a)
  }

  return (
    <div className={`container page-wide appointments-page ${resolvedRole === 'patient' ? 'client-dashboard-page' : ''}`}>
      <div className="page-head">
        <div>
          <p className="hero-kicker">
            {resolvedRole === 'specialist' ? `Professionals · ${activeStackMeta.label}` : 'Clients'}
          </p>
          <h2>{resolvedRole === 'specialist' ? 'Appointments' : 'Dashboard'}</h2>
          <p className="hint">
            {resolvedRole === 'specialist'
              ? 'Your bookings and client conversations.'
              : 'Message accepted professionals and manage bookings.'}
          </p>
        </div>
      </div>
      {renderStackSwitcher()}

      {!active && resolvedRole === 'patient' && (
        <div className="client-dashboard-grid">
          <section className="card section-card appointments-card client-dashboard-section client-accepted-section">
            <header className="client-dashboard-section-head">
              <div>
                <h3>Accepted professionals</h3>
                <p className="hint">Open chat and continue your request.</p>
              </div>
              <span className="status-pill status-accepted">{clientAcceptedAppointments.length}</span>
            </header>
            {!clientAcceptedAppointments.length && (
              <p className="hint">No accepted professionals yet.</p>
            )}
            {!!clientAcceptedAppointments.length && (
              <ul className="list client-dashboard-list">
                {clientAcceptedAppointments.map((a) => {
                  const key = getApptKey(a)
                  const reviewable = needsReview(a)
                  const title = getProviderTitleParts(a)
                  const stackLabel = PROFESSIONS[a.profession || 'doctor']?.label || 'Professional'
                  const canMessage = canOpenChat(a)
                  const unreadCount = unreadForAppointment(a)
                  return (
                    <li key={(key || a.chosenSlotISO) + String(a.feeCents)} className="item client-dashboard-item">
                      <div className="list-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
                        <div className="list-icon">
                          <CalendarCheck size={18} strokeWidth={1.8} />
                        </div>
                        <div className="list-details" style={{ flex: 1, minWidth: 240 }}>
                          <div className="list-primary inline-gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <span>{title.name}</span>
                          </div>
                          <div className="list-meta">
                            <div className="text-sm text-gray-600">{stackLabel}</div>
                            {title.label && (
                              <div className="text-sm text-gray-600">{title.label}</div>
                            )}
                            <div className="text-sm text-gray-600">
                              {formatAppointmentTime(a.chosenSlotISO, a.createdAt, a.selectionCreatedAt, localTimeZone)}
                            </div>
                          </div>
                        </div>
                        <div className="list-cta client-dashboard-actions">
                          <div className="message-action-wrap">
                            {unreadCount > 0 && (
                              <span className="message-action-alert">
                                <span className="message-action-alert-dot" aria-hidden />
                                New message
                              </span>
                            )}
                            <button
                              className={`btn btn-primary client-message-btn message-action-btn${unreadCount > 0 ? ' has-unread' : ''}`}
                              type="button"
                              onClick={() => openConversationChat(a)}
                              disabled={!canMessage}
                            >
                              <MessageSquare size={16} strokeWidth={2.2} />
                              {canMessage ? 'Message' : 'Message unavailable'}
                              {unreadCount > 0 && (
                                <span className="message-action-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                              )}
                            </button>
                          </div>
                          {reviewable && (
                            <button className="btn btn-outline" type="button" onClick={() => openDetail(a, { focusReview: true })}>
                              Leave review
                            </button>
                          )}
                          <button className="btn btn-outline" type="button" onClick={() => openDetail(a)}>
                            <ExternalLink size={16} strokeWidth={2.2} />
                            Details
                          </button>
                        </div>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      )}

      {!active && resolvedRole !== 'patient' && (
        <section className="card section-card appointments-card">
          <header>
            <div>
              <h3>Appointments</h3>
              <p className="hint">
                {filteredAppointments.length || 0}{' '}
                {filteredAppointments.length === 1 ? 'appointment' : 'appointments'}
              </p>
            </div>
          </header>
          {!filteredAppointments.length && (
            <p>No appointments.</p>
          )}
          {!!filteredAppointments.length && (
            <ul className="list">
              {filteredAppointments.map((a) => {
                const key = getApptKey(a)
                const reviewable = needsReview(a)
                const canMessage = canOpenChat(a)
                const unreadCount = unreadForAppointment(a)
                const title = resolvedRole === 'specialist'
                  ? { name: a.specialty || 'Consultation', label: '' }
                  : getProviderTitleParts(a)
                return (
                  <li key={(key || a.chosenSlotISO) + String(a.feeCents)} className="item">
                    <div className="list-row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'flex-start' }}>
                      <div className="list-icon">
                        <CalendarCheck size={18} strokeWidth={1.8} />
                      </div>
                        <div className="list-details" style={{ flex: 1, minWidth: 240 }}>
                        <div className="list-primary inline-gap-2" style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                        <span>{title.name}</span>
                          <span className={`status-pill ${statusClass(a)}`}>{displayStatus(a)}</span>
                          {reviewable && (
                            <span className="pill" style={{ background: '#fef3c7', color: '#92400e', borderColor: '#f59e0b' }}>
                              Needs review
                            </span>
                          )}
                        </div>
                        <div className="list-meta">
                          {resolvedRole !== 'specialist' && title.label && (
                            <div className="text-sm text-gray-600">{title.label}</div>
                          )}
                          <div className="text-sm text-gray-600">
                            {formatAppointmentTime(a.chosenSlotISO, a.createdAt, a.selectionCreatedAt, localTimeZone)}
                          </div>
                        </div>
                      </div>
                      <div
                        className="list-cta"
                        style={{
                          display: 'flex',
                          gap: 8,
                          flexWrap: 'wrap',
                          justifyContent: 'flex-end',
                          alignItems: 'center',
                          minWidth: 180,
                        }}
                      >
                        <div className="message-action-wrap">
                          {unreadCount > 0 && (
                            <span className="message-action-alert">
                              <span className="message-action-alert-dot" aria-hidden />
                              New message
                            </span>
                          )}
                          <button
                            className={`btn btn-primary provider-message-btn message-action-btn${unreadCount > 0 ? ' has-unread' : ''}`}
                            type="button"
                            onClick={() => openConversationChat(a)}
                            disabled={!canMessage}
                          >
                            <MessageSquare size={16} strokeWidth={2.2} />
                            {canMessage ? 'Message client' : 'Message unavailable'}
                            {unreadCount > 0 && (
                              <span className="message-action-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
                            )}
                          </button>
                        </div>
                        {reviewable && (
                          <button className="btn btn-outline" type="button" onClick={() => openDetail(a, { focusReview: true })}>
                            Leave review
                          </button>
                        )}
                        <button className="btn btn-outline" type="button" onClick={() => openDetail(a)}>
                          <ExternalLink size={16} strokeWidth={2.2} />
                          View
                        </button>
                      </div>
                    </div>
                  </li>
                )
              })}
            </ul>
          )}
        </section>
      )}

      {active && (
        <section className="card section-card appointment-focus">
          <header className="appointment-focus-head">
            <div>
              <p className="hero-kicker">Details</p>
              <h3>
                {resolvedRole === 'specialist'
                  ? (active.specialty || 'Consultation')
                  : getProviderTitleParts(active).name}
              </h3>
              <p className="hint">
                {resolvedRole !== 'specialist' && getProviderTitleParts(active).label
                  ? `${getProviderTitleParts(active).label} · `
                  : ''}
                {formatAppointmentTime(active.chosenSlotISO, active.createdAt, active.selectionCreatedAt, localTimeZone)}
                {resolvedRole === 'specialist'
                  ? ` · ${PROFESSIONS[active.profession || 'doctor'].label}`
                  : ''}
              </p>
              {showEncounterSwitch && (
                <div style={{ marginTop: 12, maxWidth: 420 }}>
                  <div className="field-label">Encounter</div>
                  <select
                    className="input"
                    value={activeEncounterId || ''}
                    onChange={(e) => {
                      const nextId = e.target.value
                      if (nextId && nextId !== activeEncounterId) {
                        nav(detailHref(nextId), { state: { fromAppointmentsList: true } })
                      }
                    }}
                  >
                    {encounterOptions.map((entry) => (
                      <option key={entry.id} value={entry.id}>
                        {entry.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="inline-gap-2">
              <button className="btn btn-outline" onClick={closeDetail}>{dismissLabel}</button>
            </div>
          </header>

          {detailNotice && (
            <div className="banner success" role="status" style={{ marginBottom: '1rem' }}>
              {detailNotice}
            </div>
          )}
          {detailError && (
            <div className="banner error" role="alert" style={{ marginBottom: '1rem' }}>
              {detailError}
            </div>
          )}

          {resolvedRole !== 'specialist' && active.reason && (
            <div className="appointment-detail-card">
              <div className="detail-label">Your brief</div>
              <div className="detail-value hint" style={{ whiteSpace: 'pre-wrap' }}>
                {active.reason}
              </div>
            </div>
          )}

          {resolvedRole !== 'specialist' && (
            <div className="appointment-detail-card">
              <div className="detail-label">Provider details</div>
              <dl className="appointment-detail-fields compact">
                <div>
                  <dt>Practice</dt>
                  <dd>{formatProviderName(active.specialist)}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>
                    <Address
                      a={
                        active.specialist?.publicContact?.address
                          ? {
                              state: active.specialist.publicContact.address.state,
                              postalCode:
                                active.specialist.publicContact.address.postalCode ||
                                active.specialist.publicContact.address.postcode ||
                                active.specialist.publicContact.address.postCode,
                          }
                          : undefined
                      }
                    />
                  </dd>
                </div>
              </dl>
              <p className="hint">Use the in-app chat for all communication.</p>
            </div>
          )}

          {resolvedRole === 'specialist' && (
            <>
              <div className="appointment-detail-card">
                <div className="detail-label">Client details</div>
                <dl className="appointment-detail-fields compact">
                  <div>
                    <dt>Name</dt>
                    <dd>{active.patient?.name || active.patientName || '—'}</dd>
                  </div>
                  <div>
                    <dt>Location</dt>
                    <dd>{locationOnly(active.patient?.address)}</dd>
                  </div>
                </dl>
              </div>

              {active.reason && (
                <div className="appointment-detail-card">
                  <div className="detail-label">Client brief</div>
                  <div className="detail-value hint" style={{ whiteSpace: 'pre-wrap' }}>{active.reason}</div>
                </div>
              )}
            </>
          )}

          {(reviewReady(active) || active.review?.specialist || active.review?.client) && (
            <div className="appointment-detail-card" ref={reviewSectionRef}>
              {resolvedRole === 'patient' ? renderPatientReviews() : renderProviderReviews()}
            </div>
          )}

          <div className="inline-gap-2" style={{ marginTop: '1rem', flexWrap: 'wrap' }}>
            {active.chosenSlotISO !== 'REQUEST_BOOKING' && (
              <a
                className="btn btn-outline"
                href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(active.specialty || 'Consultation')}&dates=${toGCalTime(active.chosenSlotISO)}/${toGCalTime(addMinutes(active.chosenSlotISO, 30))}`}
                target="_blank"
                rel="noreferrer"
              >
                Add to calendar
              </a>
            )}
            {canReschedule && (
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => {
                  setDetailError(null)
                  setDetailNotice(null)
                  setRescheduleApiError(null)
                  setPendingSlotISO(null)
                  setReschedulePaymentOpen(false)
                  setRescheduleSheetOpen(true)
                }}
                disabled={rescheduleBusy}
              >
                {rescheduleBusy ? 'Updating…' : `Reschedule (${rescheduleFeeText})`}
              </button>
            )}
            {canRebook && (
              <button
                className="btn btn-outline"
                type="button"
                onClick={() => {
                  setDetailError(null)
                  setDetailNotice(null)
                  setRebookApiError(null)
                  setPendingRebookSlotISO(null)
                  setRebookPaymentOpen(false)
                  setRebookReason('')
                  setRebookSheetOpen(true)
                }}
                disabled={rebookBusy}
              >
                {rebookBusy ? 'Sending…' : `Rebook (${rebookFeeText})`}
              </button>
            )}
          </div>
        </section>
      )}
      {active &&
        resolvedRole === 'patient' &&
        (active.specialist?.userId || active.specialistId) && (
          <SlotPickerSheet
            open={rescheduleSheetOpen}
            onClose={() => setRescheduleSheetOpen(false)}
            specialistId={active.specialist?.userId || active.specialistId || ''}
            specialistLabel={active.specialistName || active.specialist?.name || undefined}
            onPick={handleRescheduleSlotPick}
            profession={(active.profession as ProviderProfession) || 'doctor'}
            autoCloseOnPick={false}
          />
        )}
      {active &&
        resolvedRole === 'patient' &&
        (active.specialist?.userId || active.specialistId) && (
          <SlotPickerSheet
            open={rebookSheetOpen}
            onClose={() => setRebookSheetOpen(false)}
            specialistId={active.specialist?.userId || active.specialistId || ''}
            specialistLabel={active.specialistName || active.specialist?.name || undefined}
            onPick={handleRebookSlotPick}
            profession={(active.profession as ProviderProfession) || 'doctor'}
            autoCloseOnPick={false}
          />
        )}
      {active && resolvedRole === 'patient' && (
        <ReschedulePaymentSheet
          open={reschedulePaymentOpen && Boolean(pendingSlotISO)}
          slotISO={pendingSlotISO}
          onClose={() => {
            setReschedulePaymentOpen(false)
            setPendingSlotISO(null)
            setRescheduleApiError(null)
          }}
          onChangeSlot={() => {
            setReschedulePaymentOpen(false)
            setRescheduleSheetOpen(true)
          }}
          onSubmit={handleReschedulePayment}
          busy={rescheduleBusy}
          apiError={rescheduleApiError}
          currencyCode={appointmentCurrencyCode}
          feeCents={PATIENT_RESCHEDULE_FEE_CENTS}
          timeZone={localTimeZone}
        />
      )}
      {active && resolvedRole === 'patient' && (
        <RebookPaymentSheet
          open={rebookPaymentOpen && Boolean(pendingRebookSlotISO)}
          slotISO={pendingRebookSlotISO}
          reason={rebookReason}
          onReasonChange={setRebookReason}
          onClose={() => {
            setRebookPaymentOpen(false)
            setPendingRebookSlotISO(null)
            setRebookReason('')
            setRebookApiError(null)
          }}
          onChangeSlot={() => {
            setRebookPaymentOpen(false)
            setRebookSheetOpen(true)
          }}
          onSubmit={handleRebookPayment}
          busy={rebookBusy}
          apiError={rebookApiError}
          currencyCode={appointmentCurrencyCode}
          feeCents={PATIENT_REBOOK_FEE_CENTS}
          timeZone={localTimeZone}
        />
      )}
    </div>
  )
}

type ReschedulePaymentSheetProps = {
  open: boolean
  slotISO: string | null
  busy: boolean
  apiError: string | null
  currencyCode: string
  feeCents: number
  timeZone: string
  onSubmit: (paymentMethodId: string) => Promise<void>
  onClose: () => void
  onChangeSlot: () => void
}

function ReschedulePaymentSheet({
  open,
  slotISO,
  busy,
  apiError,
  currencyCode,
  feeCents,
  timeZone,
  onSubmit,
  onClose,
  onChangeSlot,
}: ReschedulePaymentSheetProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = React.useState('')
  const [cardError, setCardError] = React.useState<string | null>(null)
  const [cardBusy, setCardBusy] = React.useState(false)
  const slotLabel = slotISO ? fmtDateTimeLocal(slotISO, timeZone) : ''
  const actionFeeLabel = feeLabel(feeCents, currencyCode)

  React.useEffect(() => {
    if (!open) {
      setName('')
      setCardError(null)
      setCardBusy(false)
    }
  }, [open])

  if (!open) return null

  const handleSubmit = async () => {
    if (!stripe || !elements || !slotISO) return
    const card = elements.getElement(CardElement)
    if (!card) return
    setCardError(null)
    setCardBusy(true)
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: { name: name || undefined },
      })
      if (error || !paymentMethod?.id) {
        throw new Error(error?.message || 'Card error')
      }
      await onSubmit(paymentMethod.id)
    } catch (err: any) {
      setCardError(err?.message || 'Payment failed')
    } finally {
      setCardBusy(false)
    }
  }

  const disabled = cardBusy || busy || !stripe

  return (
    <div
      className="overlay show"
      role="dialog"
      aria-modal="true"
      aria-label="Confirm reschedule"
      onClick={onClose}
    >
      <div className="sheet" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="sheet-header">
          <div className="sheet-title">Confirm reschedule</div>
          <button className="btn close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="sheet-body">
          {cardError && (
            <div className="banner error" role="alert" style={{ marginBottom: '0.75rem' }}>
              {cardError}
            </div>
          )}
          {apiError && (
            <div className="banner error" role="alert" style={{ marginBottom: '0.75rem' }}>
              {apiError}
            </div>
          )}
          <PaymentCard
            amount={actionFeeLabel}
            blurb="A one-off change fee keeps your provider notified and covers admin costs."
            summaryItems={[
              { label: 'New time', value: slotLabel || 'Pick a slot' },
              { label: 'Change fee', value: actionFeeLabel },
            ]}
            note="We’ll only charge this after you submit the new time."
          >
            <div>
              <div className="field-label">Name on card</div>
              <input
                className="input"
                placeholder="Full name"
                value={name}
                onChange={e => setName(e.target.value)}
                disabled={disabled}
              />
            </div>
            <div>
              <div className="field-label">Card details</div>
              <div className="stripe-input">
                <CardElement options={{ hidePostalCode: true }} />
              </div>
            </div>
          </PaymentCard>
          <div className="hint" style={{ marginTop: '0.5rem' }}>
            Need a different slot?{' '}
            <button className="btn btn-ghost btn-compact" type="button" onClick={onChangeSlot} style={{ padding: 0 }}>
              Pick another time
            </button>
          </div>
        </div>
        <div className="sheet-footer">
          <button className="btn" onClick={onClose} disabled={disabled}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={disabled || !slotISO}>
            {disabled ? 'Processing…' : `Pay ${actionFeeLabel} & reschedule`}
          </button>
        </div>
      </div>
    </div>
  )
}

type RebookPaymentSheetProps = {
  open: boolean
  slotISO: string | null
  reason: string
  busy: boolean
  apiError: string | null
  currencyCode: string
  feeCents: number
  timeZone: string
  onReasonChange: (value: string) => void
  onSubmit: (paymentMethodId: string, reason: string) => Promise<void>
  onClose: () => void
  onChangeSlot: () => void
}

function RebookPaymentSheet({
  open,
  slotISO,
  reason,
  busy,
  apiError,
  currencyCode,
  feeCents,
  timeZone,
  onReasonChange,
  onSubmit,
  onClose,
  onChangeSlot,
}: RebookPaymentSheetProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = React.useState('')
  const [cardError, setCardError] = React.useState<string | null>(null)
  const [reasonError, setReasonError] = React.useState<string | null>(null)
  const [cardBusy, setCardBusy] = React.useState(false)
  const slotLabel = slotISO ? fmtDateTimeLocal(slotISO, timeZone) : ''
  const actionFeeLabel = feeLabel(feeCents, currencyCode)

  React.useEffect(() => {
    if (!open) {
      setName('')
      setCardError(null)
      setReasonError(null)
      setCardBusy(false)
    }
  }, [open])

  if (!open) return null

  const disabled = cardBusy || busy || !stripe

  const handleSubmit = async () => {
    if (!reason.trim()) {
      setReasonError('Please provide a brief reason for the follow-up.')
      return
    }
    if (!stripe || !elements || !slotISO) return
    const card = elements.getElement(CardElement)
    if (!card) return
    setCardError(null)
    setReasonError(null)
    setCardBusy(true)
    try {
      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card,
        billing_details: { name: name || undefined },
      })
      if (error || !paymentMethod?.id) {
        throw new Error(error?.message || 'Card error')
      }
      await onSubmit(paymentMethod.id, reason)
    } catch (err: any) {
      setCardError(err?.message || 'Payment failed')
    } finally {
      setCardBusy(false)
    }
  }

  return (
    <div
      className="overlay show"
      role="dialog"
      aria-modal="true"
      aria-label="Rebook appointment"
      onClick={onClose}
    >
      <div className="sheet" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
        <div className="sheet-header">
          <div className="sheet-title">Rebook follow-up</div>
          <button className="btn close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>
        <div className="sheet-body">
          {reasonError && (
            <div className="banner error" role="alert" style={{ marginBottom: '0.75rem' }}>
              {reasonError}
            </div>
          )}
          {cardError && (
            <div className="banner error" role="alert" style={{ marginBottom: '0.75rem' }}>
              {cardError}
            </div>
          )}
          {apiError && (
            <div className="banner error" role="alert" style={{ marginBottom: '0.75rem' }}>
              {apiError}
            </div>
          )}
          <PaymentCard
            amount={actionFeeLabel}
            blurb="A quick follow-up fee keeps your provider in the loop."
            summaryItems={[
              { label: 'Requested time', value: slotLabel || 'Pick a slot' },
              { label: 'Rebook fee', value: actionFeeLabel },
            ]}
          >
            <div>
              <div className="field-label">Why are you rebooking?</div>
              <textarea
                className="input"
                placeholder="e.g., Need a follow-up session next week…"
                rows={3}
                value={reason}
                onChange={(e) => {
                  onReasonChange(e.target.value)
                  setReasonError(null)
                }}
                disabled={disabled}
              />
            </div>
            <div>
              <div className="field-label">Name on card</div>
              <input
                className="input"
                placeholder="Full name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={disabled}
              />
            </div>
            <div>
              <div className="field-label">Card details</div>
              <div className="stripe-input">
                <CardElement options={{ hidePostalCode: true }} />
              </div>
            </div>
          </PaymentCard>
          <div className="hint" style={{ marginTop: '0.5rem' }}>
            Need a different slot?{' '}
            <button className="btn btn-ghost btn-compact" type="button" onClick={onChangeSlot} style={{ padding: 0 }}>
              Pick another time
            </button>
          </div>
        </div>
        <div className="sheet-footer">
          <button className="btn" onClick={onClose} disabled={disabled}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={disabled || !slotISO}>
            {disabled ? 'Processing…' : `Pay ${actionFeeLabel} & request`}
          </button>
        </div>
      </div>
    </div>
  )
}

function addMinutes(iso: string, minutes: number) {
  const d = new Date(iso)
  d.setMinutes(d.getMinutes() + minutes)
  return d.toISOString()
}

function toGCalTime(iso: string) {
  const d = new Date(iso)
  const pad = (n: number) => String(n).padStart(2, '0')
  return [
    d.getUTCFullYear(),
    pad(d.getUTCMonth() + 1),
    pad(d.getUTCDate()),
    'T',
    pad(d.getUTCHours()),
    pad(d.getUTCMinutes()),
    pad(d.getUTCSeconds()),
    'Z',
  ].join('')
}
